/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'next/router';
import { connectInfiniteHits } from 'react-instantsearch-dom';
import { InView } from 'react-intersection-observer';

import Link from 'next/link';
import { css } from '@emotion/core';
import Hit from './plp-product-card';
import Button from '../../atoms/button';

import { useSearch } from '../../utils/context/search-provider';
import { updateAlgoliaWishlist } from '../../store/modules/lists/actions';
import Container from '../../utils/content-container';
import GA4 from '../../utils/ga4DataLayer';
import { mapProductToGA4Item } from '../../utils/ga4ItemMapper';

export const ProductGrid = styled.div(({ theme }) => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'repeat(2, 1fr)',
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    gridTemplateColumns: 'repeat(auto, 1fr)'
  }
}));

const ResultsGrid = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.5rem;
  & a + div:not([data-scrollingpagenum="1"]) {
    margin-top: 0rem;
  };
`;

const GridButtonAnchor = styled.a`
  width: fit-content;
  text-decoration: none;
  text-align: center;
  align-self: center;

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    // keeps the element roughly the same size as a product card
    width: 33%;
  }
`;

const GridButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isLoadMore'
})(({ theme, isLoadMore }) => ({
  whiteSpace: 'nowrap',
  width: '100%',
  margin: isLoadMore ? '0 0 2rem 0' : '2rem 0 0 0',
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    margin: '0 auto'
  }
}));

const GridSentinel = styled(InView)``;

const FILTERED_PAGE_SENTINEL_EMO_PROPS = new Set(['isWishlistShareable', 'hasPrevious']);

const PageSentinel = styled(InView, {
  shouldForwardProp: (prop) => !FILTERED_PAGE_SENTINEL_EMO_PROPS.has(prop)
})`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(${({ isWishlistShareable }) =>
    (isWishlistShareable ? '2' : 'auto-fill')}, minmax(50% , 1fr));
  overflow-x: hidden;
  // adds top border when the load previous button is present to complete product grid
  ${({ theme, hasPrevious }) => (hasPrevious && `border-top: 1px solid ${theme.color.lightGrey}`)};
  &:not([data-scrollingpagenum="1"]) {
    margin-top: -1.5rem;
  };

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.medium}) {
    grid-template-columns: repeat(auto-fill, minmax(30% , 1fr));
    border-top: none;
    &:not([data-scrollingpagenum="1"]) {
      margin-top: 0rem;
    };
    ${({ isWishlistShareable }) => isWishlistShareable && css`
      grid-gap: 3.6rem;
  `}
  }
  ${({ isWishlistShareable }) => isWishlistShareable && css`
    grid-gap: 1.6rem;
    padding: 1.6rem;
  `}
`;

const Loading = styled.span(({ theme }) => ({
  display: 'block',
  gridArea: 'loader',
  height: '6rem',
  position: 'relative',
  width: '100%',
  '::after': {
    animation: 'spin .5s infinite linear',
    border: `0.25rem solid ${theme.color.brand}`,
    borderRadius: 290486,
    borderRightColor: 'transparent',
    borderTopColor: 'transparent',
    content: '""',
    height: '3rem',
    left: 'calc(50% - (3rem / 2))',
    position: 'absolute',
    top: 'calc(50% - (3rem / 2))',
    width: '3rem'
  }
}));

const InfiniteHits = (props) => {
  const {
    state: {
      scrollPage,
      scrollSlug
    },
    resetScrollPage,
    resetScrollSlug
  } = useSearch();

  const [activeQuickShopID, setActiveQuickshopID] = useState(-1);
  const [viewNext, setViewNext] = useState(false);
  const [viewPrev, setViewPrev] = useState(false);
  const [newSearch, setNewSearch] = useState(true);
  const [minPageNum, setMinPageNum] = useState(props.currentPage);
  const [maxPageNum, setMaxPageNum] = useState(props.currentPage);
  const [currentQuery, setCurrentQuery] = useState(props.currentQuery);
  const [currentHierMenu, setCurrentHierMenu] = useState(props.currentHierarchicalMenu);
  const [currentRefinements, setCurrentRefinements] = useState(props.currentRefinements);
  const [currentSort, setCurrentSort] = useState(props.currentSort);
  const [displayPreviousLoading, setDisplayPreviousLoading] = useState(false);
  const [wishedProductsToShareStatus, wishedProductsFromAlgolia] = useSelector((state) =>
    [state.lists.wishedProductsToShareStatus, state.lists.wishedProductsFromAlgolia]);
  const dispatch = useDispatch();

  // TODO: remove after wishlist pagination is completed
  const [isWishlist, setIsWishlist] = useState(false);
  // groupHitsByPage was memoized with useCallback, because it's being
  // used inside the useEffect, to avoid unnecessary redeclarations from the function
  const groupHitsByPage = useCallback(() => props.hits.reduce((pagesArray, hit, idx) => {
    if (idx % props.hitsPerPage === 0) {
      pagesArray.push([hit]);
    } else {
      pagesArray[pagesArray.length - 1].push(hit);
    }

    return pagesArray;
  }, []), [props.hits, props.hitsPerPage]);

  const generatePageNumber = (index) => minPageNum + index;

  const finishedLoadingPrevResults = (pageNum) => {
    // to handle the loading spinner for cached results,
    // we can check if scrollPage is truthy value.
    // if it is, that means the user has visited this PLP before
    if ((scrollPage && scrollPage.length)
    && (pageNum === minPageNum)
    && displayPreviousLoading) {
      setDisplayPreviousLoading(false);
    }
  };

  const onNextSentinelIntersection = (inView, entry) => {
    const { hasMore, refineNext } = props;

    if (entry.isIntersecting && hasMore && viewNext) {
      setMaxPageNum(maxPageNum + 1);
      refineNext();
    }
  };

  const onPageSentinelIntersection = (inView, entry) => {
    if (inView) {
      const { asPath } = props.router || {};
      const currentPath = asPath || '';
      const [path, queryParams] = currentPath.split('?');
      const searchParams = new global.URLSearchParams(queryParams);
      const currentPageOnURL = searchParams.get('page');
      const currentPageInView = parseInt(entry.target.dataset.scrollingpagenum, 10);

      if (
        !isWishlist // TODO: remove after wishlist pagination is implemented
        && (parseInt(currentPageOnURL, 10) !== currentPageInView && !newSearch)) {
        searchParams.set('page', entry.target.dataset.scrollingpagenum);

        if (currentPageInView === 1) {
          searchParams.delete('page');
        }
        const newQueryParams = searchParams.toString();
        const url = newQueryParams === '' ? path : `${path}?${newQueryParams}`;
        props.router.push(url, undefined, { shallow: true });
      }
    }
  };

  const handleActiveQuickshopID = (id) => {
    setActiveQuickshopID(id);
  };

  const getUrlPath = (direction) => {
    const { asPath } = props.router || {};
    const currentPath = asPath || '';
    const [path, queryParams] = currentPath.split('?');
    const searchParams = new global.URLSearchParams(queryParams);

    let newPage;

    if (typeof direction === 'number') {
      newPage = direction;
    } else {
      newPage = direction === 'prev' ? minPageNum - 1 : maxPageNum + 1;
    }

    searchParams.set('page', newPage);
    const newQueryParams = searchParams.toString();
    return `${path}?${newQueryParams}`;
  };

  const searchStateUpdated = () => {
    const refinementsMatch = () => {
      const newRefine = props.currentRefinements;
      const currentRefine = currentRefinements;

      // retrieve all the applied filters from both refinements
      const newfilters = Object.keys(newRefine);
      const currentFilters = Object.keys(currentRefine);

      // to short circuit, we can check if
      // the total number of filters do not match
      if (!newfilters.length && currentFilters.length) return false;

      // check if each new filter exists in the current refinements.
      // if it does exist & the total num of values are the same,
      // then iterate through the new filter's values
      // and check if each value exists in the current filter's values
      return newfilters.every((filter) => (
        currentRefine[filter]
        && newRefine[filter].length === currentRefine[filter].length
        && newRefine[filter].every((v) => currentRefine[filter].includes(v))));
    };

    return (props.currentQuery !== currentQuery
      || props.currentHierarchicalMenu !== currentHierMenu
      || !refinementsMatch(props.currentRefinements, currentRefinements))
      || props.currentSort !== currentSort;
  };

  const scrollToLastProductClicked = () => {
    const currentPath = props.router?.asPath || '';
    if (scrollPage && scrollPage === currentPath && scrollSlug.length) {
      const elem = global.document.querySelector(`[data-productslug="${scrollSlug}"]`);

      if (elem) {
        setTimeout(() => {
          elem.scrollIntoView({ block: 'center' });
        }, 0);
      }
    }
  };

  useEffect(() => {
    // TODO: remove after wishlist pagination is implemented
    if (props.router.pathname === ('/lists/wishlist')) {
      if (!isWishlist) setIsWishlist(true);
      if (groupHitsByPage()?.flat()?.length > 0) {
        // in case of products returned by groupHitsByPage are greater than 0
        // it's flattened and stored
        const products = groupHitsByPage().flat();
        const algoliaWishList = products.map((product) => ({
          productId: product?.objectID,
          productSku: product?.manufacturer_id,
          name: product?.title,
          badges: product?.trends,
          category: product?.product_type,
          brand: product?.brand
        }));
        // avoid unnecessary updates from algolia wishlist global state
        if (algoliaWishList.toString() !== wishedProductsFromAlgolia.toString()) {
          dispatch(updateAlgoliaWishlist(algoliaWishList));
        }
      }
    }

    scrollToLastProductClicked();
  }, [dispatch, groupHitsByPage, isWishlist]);

  useEffect(() => {
    // if user filtered on PLP with brands or is on /lists/wishlist,
    // there is a delay in receiving the correct hits from algolia
    if ((!isWishlist && props.currentRefinements.brand)
    || (isWishlist)
    ) {
      scrollToLastProductClicked();
    }
  }, [props.hits]);

  useEffect(() => {
    // to handle loading state if no cached results.
    // if scrollPage is falsy, then this is the first
    // time the user has visited this PLP
    if (!isWishlist // TODO: remove after wishlist pagination is implemented
      && !scrollPage
      && !props.isSearchStalled && viewPrev) {
      setDisplayPreviousLoading(false);
      setViewPrev(false);
    }
  }, [isWishlist, props.isSearchStalled]);

  useEffect(() => {
    // this is a waterfall effect for the loading wheel
    // and the Prev Button.
    // if both displayPreviousLoading & viewPrev were
    // set to false at the same time,
    // then the user would not be able to see the loading wheel
    if (!displayPreviousLoading && viewPrev) {
      setViewPrev(false);
    }
  }, [displayPreviousLoading]);

  useEffect(() => {
    if (
      !isWishlist // TODO: remove after wishlist pagination is implemented
      && props.router
      && props.router.asPath
    ) {
      const { asPath } = props.router || {};
      const currentPath = asPath || '';
      const queryParams = currentPath.split('?')[1];
      const searchParams = new global.URLSearchParams(queryParams);
      const currentPageOnURL = parseInt(searchParams.get('page'), 10);

      // we need this conditional because
      // isSearchStalled does not return false
      // when the user scrolls back up to page 1
      if (
        currentPageOnURL === 1
        && !props.isSearchStalled
        && !props.hasPrevious
        && viewPrev && displayPreviousLoading
      ) {
        setViewPrev(false);
        setDisplayPreviousLoading(false);
      }
    }
  }, [props.router]);

  useEffect(() => {
    // to reset all local state if new search or filters or hierarchical menu changed
    if (
      !isWishlist
      // TODO: remove after wishlist pagination is implemented
      && searchStateUpdated()
      && props.currentPage === 1
    ) {
      setNewSearch(true);
      resetScrollPage();
      resetScrollSlug();
      setCurrentQuery(props.currentQuery);
      setCurrentHierMenu(props.currentHierarchicalMenu);
      setCurrentRefinements(props.currentRefinements);
      setCurrentSort(props.currentSort);
      setMinPageNum(props.currentPage);
      setMaxPageNum(props.currentPage);
      setViewNext(false);
      setViewPrev(false);
      setDisplayPreviousLoading(false);
    }
  }, [
    props.currentQuery,
    props.currentHierarchicalMenu,
    props.currentRefinements,
    props.currentPage
  ]);

  // Add GA4 tracking for product list view
  useEffect(() => {
    // Only track when we have hits and it's a new search (not infinite scroll)
    if (props.hits && props.hits.length > 0 && props.currentPage === 1) {
      const ga4Items = props.hits.map((product, index) => 
        mapProductToGA4Item(product, index)
      );
      
      // Determine list ID and name based on current page
      let itemListId = 'product_list';
      let itemListName = 'Product List';
      
      const currentPath = props.router?.asPath || '';
      
      if (isWishlist) {
        itemListId = 'wishlist';
        itemListName = 'Wishlist';
      } else if (currentPath.includes('/search')) {
        itemListId = 'search_results';
        itemListName = 'Search Results';
      } else if (currentPath.includes('/brands/')) {
        itemListId = 'brand_page';
        itemListName = 'Brand Page';
      } else if (currentPath.includes('/category/')) {
        itemListId = 'category_page';
        itemListName = 'Category Page';
      }
      
      GA4.viewItemList({
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: ga4Items
      });
    }
  }, [props.currentPage, props.router?.asPath, isWishlist]);

  return (
    <ResultsGrid>
      {props.hasPrevious && !viewPrev && (
        <Link href={getUrlPath('prev')} passHref>
          <GridButtonAnchor onClick={(e) => e.preventDefault()}>
            <GridButton
              type="button"
              onClick={() => {
                if (!viewPrev) {
                  setNewSearch(false);
                  setMinPageNum(minPageNum - 1);
                  props.refinePrevious();
                  setDisplayPreviousLoading(true);
                  setViewPrev(true);
                }
              }}
            >
              Load Previous
            </GridButton>
          </GridButtonAnchor>
        </Link>
      )}

      {displayPreviousLoading && <Loading />}

      {groupHitsByPage().map((page, i) => (
        <PageSentinel
          key={`page-${generatePageNumber(i)}`}
          data-scrollingpagenum={generatePageNumber(i)}
          threshold={0}
          isWishlistShareable={wishedProductsToShareStatus}
          onChange={onPageSentinelIntersection}
          hasPrevious={props.hasPrevious || displayPreviousLoading}
        >
          {page.map((hit, idx) => (
            <Hit
              idx={idx}
              key={hit.objectID}
              hit={hit}
              activeQuickShopID={activeQuickShopID}
              setActiveQuickshopID={handleActiveQuickshopID}
              pageSlug={
                !isWishlist
                  ? getUrlPath(generatePageNumber(i))
                  : '/lists/wishlist'
              }
              hitPageNumber={generatePageNumber(i)}
              isWishlistEnabled
              removeLoadingState={finishedLoadingPrevResults}
              sponsored={props.sponsoredProducts.includes(hit.objectID)}
              meta={props.meta}
              growthbookFeatureFlagValues={props.growthbookFeatureFlagValues}
            />
          ))}
        </PageSentinel>
      ))}

      {viewNext && props.hasMore && (
        <GridSentinel threshold={0} onChange={onNextSentinelIntersection}>
          <Loading />
        </GridSentinel>
      )}

      {props.hasMore && !viewNext && (
        <Link href={getUrlPath('next')} passHref>
          <GridButtonAnchor onClick={(e) => e.preventDefault()}>
            <GridButton
              type="button"
              isLoadMore
              onClick={() => {
                if (!viewNext) {
                  setNewSearch(false);
                  props.refineNext();
                  setViewNext(true);
                }
              }}
            >
              Load More
            </GridButton>
          </GridButtonAnchor>
        </Link>
      )}
     {props.router && (<Container router={props.router}/>)}
    </ResultsGrid>
  );
};

// TODO: remove defaultProp values after wishlist pagination is implemented
InfiniteHits.defaultProps = {
  currentPage: 1,
  hitsPerPage: 24,
  currentQuery: '',
  currentHierarchicalMenu: '',
  currentRefinements: {},
  currentSort: '',
  isSearchStalled: false,
  sponsoredProducts: [],
  meta: {},
  growthbookFeatureFlagValues: {},
};

InfiniteHits.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object).isRequired,
  hitsPerPage: PropTypes.number,
  hasPrevious: PropTypes.bool.isRequired,
  refinePrevious: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  refineNext: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  currentPage: PropTypes.number,
  currentQuery: PropTypes.string,
  currentHierarchicalMenu: PropTypes.string,
  currentRefinements: PropTypes.object,
  currentSort: PropTypes.string,
  isSearchStalled: PropTypes.bool,
  sponsoredProducts: PropTypes.array,
  meta: PropTypes.object,
  growthbookFeatureFlagValues: PropTypes.object,
};

export default withRouter(connectInfiniteHits(InfiniteHits));
